import { useCallback, useEffect, useMemo, useState } from 'react';

import { RoundedButton } from '@genially/design-system';
import CloseSmallIcon from '@genially/icons/lib/close-small/CloseSmallIcon';
import FullSizeIcon from '@genially/icons/lib/ful-size/FulSizeIcon';
import MinimizeIcon from '@genially/icons/lib/minimize/MinimizeIcon';

import { useSidebarSummaryContext } from '../../SidebarSummary.context';
import { SidebarType } from '../../types/SidebarType';
import { LastUpdatedInfo } from './components/LastUpdatedInfo/LastUpdatedInfo';
import { OpenAnswerSummary } from './components/OpenAnswerSummary/OpenAnswerSummary';
import { RankingSummary } from './components/RankingSummary/RankingSummary';
import { SurveySummaryData } from './components/SurveySummary/SurveySummaryData';
import { SidebarSummaryContentProvider } from './SidebarSummaryWithContent.context';
import {
  ContentWrapper,
  FooterWrapper,
  LastUpdateWrapper,
  SizeButtonWrapper,
  StyledReplaceIcon,
  StyledSmallParagraph,
  TextWrapper,
} from '../../SidebarSummary.styled';

export const SidebarSummaryWithContent = ({
  showLastAnimation,
  isFullWidthActive,
  handleClose,
  handleMaximizeMinimize,
  handleRefresh,
}: {
  showLastAnimation: boolean;
  isFullWidthActive: boolean;
  handleClose: () => void;
  handleMaximizeMinimize: () => void;
  handleRefresh: () => void;
}) => {
  const {
    type,
    config: { getTranslation, hideLastUpdated },
  } = useSidebarSummaryContext();

  const [isEnabledFullScreenButton, setIsEnabledFullScreenButton] = useState(true);

  useEffect(() => {
    if (showLastAnimation) {
      setIsEnabledFullScreenButton(false);
    }
  }, [showLastAnimation]);

  const handleRankingLastAnimationEnd = useCallback(() => {
    if (!isEnabledFullScreenButton) {
      setIsEnabledFullScreenButton(true);
    }
  }, [isEnabledFullScreenButton]);

  const content = useMemo(() => {
    switch (type) {
      case SidebarType.OpenAnswer:
        return <OpenAnswerSummary isFullWidthEnabled={isFullWidthActive} />;
      case SidebarType.Survey:
        return <SurveySummaryData isFullWidthEnabled={isFullWidthActive} />;
      case SidebarType.Ranking:
        return (
          <RankingSummary
            isFullWidthEnabled={isFullWidthActive}
            isAfterLastAnimation={showLastAnimation}
            handleLastAnimationEnd={handleRankingLastAnimationEnd}
          />
        );
      default:
        console.error(`Sidebar type ${type} not supported`);
        return null;
    }
  }, [handleRankingLastAnimationEnd, isFullWidthActive, showLastAnimation, type]);

  return (
    <SidebarSummaryContentProvider>
      <SizeButtonWrapper $hiddenFullWidth={isFullWidthActive}>
        <RoundedButton
          Icon={<CloseSmallIcon />}
          size={RoundedButton.Size.SMALL}
          onClick={handleClose}
        />
      </SizeButtonWrapper>

      <SizeButtonWrapper $hiddenMobile $hidden={!isEnabledFullScreenButton}>
        <RoundedButton
          Icon={isFullWidthActive ? <MinimizeIcon /> : <FullSizeIcon />}
          size={RoundedButton.Size.SMALL}
          onClick={handleMaximizeMinimize}
        />
      </SizeButtonWrapper>
      <ContentWrapper>{content}</ContentWrapper>
      {!hideLastUpdated ? (
        <FooterWrapper>
          <LastUpdateWrapper>
            <TextWrapper>
              <StyledSmallParagraph>
                {getTranslation(
                  `__new.content.interactiveQuestion.sidebar.ranking.lastUpdate`,
                )}
              </StyledSmallParagraph>
              <LastUpdatedInfo />
            </TextWrapper>
            <StyledReplaceIcon onClick={handleRefresh} />
          </LastUpdateWrapper>
        </FooterWrapper>
      ) : undefined}
    </SidebarSummaryContentProvider>
  );
};
